import { TMultipartUploadProgress } from '@/shared/hooks/use-multipart-upload/use-multipart-upload.types';
import {
  S3MultipartCompleteInfoInput,
  S3MultipartUploadUrlType,
} from '@/shared/types/types.generated';

const MAX_CHUNK_SIZE = 100 * 1024 * 1024; // 100MB

export const useMultipartUpload = () => {
  const calculatePartsCount = (fileSize: number): number => {
    if (fileSize <= 0) {
      return 0;
    }
    return Math.ceil(fileSize / MAX_CHUNK_SIZE);
  };

  const uploadFileChunks = async (
    uploadUrls: S3MultipartUploadUrlType[],
    file: File,
    onProgress?: (progress: TMultipartUploadProgress) => void,
  ): Promise<S3MultipartCompleteInfoInput[]> => {
    const uploadedBytes: Record<number, number> = {};

    const updateProgress = () => {
      if (onProgress) {
        const totalLoaded = Object.values(uploadedBytes).reduce(
          (a, b) => a + b,
          0,
        );
        const percentage =
          file.size > 0 ? Math.round((totalLoaded / file.size) * 100) : 0;
        onProgress({
          loaded: totalLoaded,
          total: file.size,
          percentage,
        });
      }
    };

    const uploadPromises = uploadUrls.map(async (url, index) => {
      const start = index * MAX_CHUNK_SIZE;
      const end = Math.min(start + MAX_CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      if (!chunk || chunk.size === 0) {
        return null;
      }

      uploadedBytes[url.partNumber] = 0;
      updateProgress();

      const etag = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            uploadedBytes[url.partNumber] = event.loaded;
            updateProgress();
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader('ETag');
            if (etag) {
              uploadedBytes[url.partNumber] = chunk.size;
              updateProgress();
              resolve(etag);
            } else {
              reject(new Error('ETag not found in response'));
            }
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload aborted'));
        });

        xhr.open('PUT', url.uploadUrl);
        xhr.send(chunk);
      });

      return {
        etag,
        partNumber: url.partNumber,
      };
    });

    const results = await Promise.all(uploadPromises);

    return results.filter(
      (result): result is S3MultipartCompleteInfoInput => result !== null,
    );
  };

  const uploadFile = async (
    uploadUrls: S3MultipartUploadUrlType[],
    file: File,
    onProgress?: (progress: TMultipartUploadProgress) => void,
  ): Promise<S3MultipartCompleteInfoInput[]> => {
    return await uploadFileChunks(uploadUrls, file, onProgress);
  };

  return {
    uploadFile,
    calculatePartsCount,
  };
};
